import React, { useCallback, useId, useState } from 'react';
import { ModalPageView } from '@cotera/client/app/components/app';
import { useModalManager } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';

const RenameColumnForm: React.FC<{
  columnLabel: string;
  takenNames: ReadonlySet<string>;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}> = ({ columnLabel, takenNames, onSubmit, onCancel }) => {
  const [draft, setDraft] = useState(columnLabel);
  const name = draft.trim();
  // Named rather than folded into `canSubmit`, for the reason the add-column
  // form gives: a disabled button with no reason beside it leaves the user
  // retyping the same name and watching nothing happen.
  const duplicate = name !== '' && name !== columnLabel && takenNames.has(name);
  const canSubmit = name !== '' && name !== columnLabel && !duplicate;
  const errorId = useId();

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit) {
          onSubmit(name);
        }
      }}
    >
      <div className="space-y-1.5">
        <label
          htmlFor="dataset-rename-column"
          className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
        >
          Name
        </label>
        <Input
          id="dataset-rename-column"
          autoFocus
          aria-label="Column name"
          aria-invalid={duplicate}
          aria-describedby={duplicate ? errorId : undefined}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        {duplicate ? (
          <span id={errorId} role="alert" className="text-xs text-destructive">
            A column named “{name}” already exists
          </span>
        ) : null}
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!canSubmit}>
          Rename column
        </Button>
      </div>
    </form>
  );
};

/**
 * Asks what to rename a dataset column to, resolving the new name — or null
 * when the user backs out.
 *
 * A prompt rather than an inline edit in the header: a rename persists a
 * `rename_column` operation that every later read is addressed by, so it is
 * worth a deliberate confirm, and the form is where the "name already taken"
 * answer has room to be given.
 */
export function useRenameColumn(): (input: {
  columnLabel: string;
  takenNames: ReadonlySet<string>;
}) => Promise<string | null> {
  const modalManager = useModalManager((s) => s.actions);

  return useCallback(
    ({ columnLabel, takenNames }) =>
      new Promise<string | null>((resolve) => {
        let settled = false;
        const settle = (name: string | null): void => {
          if (settled) {
            return;
          }
          settled = true;
          resolve(name);
        };

        modalManager.push({
          view: () => (
            <ModalPageView
              title="Rename column"
              subtitle={columnLabel}
              className="px-4 py-4"
            >
              <RenameColumnForm
                columnLabel={columnLabel}
                takenNames={takenNames}
                onSubmit={(name) => {
                  settle(name);
                  modalManager.pop();
                }}
                onCancel={() => {
                  settle(null);
                  modalManager.pop();
                }}
              />
            </ModalPageView>
          ),
          // Escape or a backdrop click leaves the column named as it was.
          onClose: () => settle(null),
        });
      }),
    [modalManager]
  );
}
